import { useMemo, useState } from 'react'
import { TeacherAccountState } from '../../firebase/types-teacher'
import { HowDidYouHearOptions } from '../../firebase/types'
import {
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Stack,
    useMediaQuery,
} from '@mui/material'
import { modalStyle } from '../../pages/Dashboard/RegistrationModals'
import pxToRem from '../../helpers/pxToRem'
import styles from './index.module.scss'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/store'
import sparkLogo from '../../assets/images/share/sparkIcon.png'
import { PrimaryButton } from '../Button'
import useTextInput from '../../hooks/useTextInput'
import { getErrorMsgForTextInput } from '../../helpers/inputValidators'
import { TeacherRegFormControl } from '../FormControl'
import { UserRegTextField } from '../TextField'
import { submitHowDidYouHear } from '../../firebase/general-apis'
import { logger } from '../../helpers/logger'

const pcStyling = {
    ...modalStyle,
    height: pxToRem(669),
    maxHeight: '90%',
    width: pxToRem(1158),
    maxWidth: '90%',
    backgroundColor: styles.primaryPurple500,
    padding: pxToRem(40),
    gap: pxToRem(40),
    position: 'relative' as 'relative',
    overflow: 'auto',
    flexDirection: 'row' as 'row',
    alignItems: 'center',
    // backgroundImage: `url(${(<svg width="1158" height="669" viewBox="0 0 1158 669" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M1652.71 462.006C1819.02 433.159 1973.06 573.299 1944.46 739.612C1923.54 861.287 1892.26 978.949 1841.78 1044.14C1739.41 1176.36 1617.38 1223.73 1457.2 1294.49C1297.02 1365.26 1120.02 1397.76 943.293 1388.84C766.57 1379.92 596.102 1329.9 448.361 1243.6C300.62 1157.3 180.6 1037.64 99.8989 896.188C19.1977 754.734 -19.4566 596.267 -12.329 436.098C-5.20142 275.93 47.4671 119.475 140.587 -18.1493C189.297 -90.1378 248.213 -155.701 315.532 -213.26C420.873 -303.328 575.46 -258.26 648.235 -140.066L681.417 -86.1749C744.618 16.4703 699.868 149.824 632.419 249.508C591.559 309.895 568.449 378.546 565.321 448.825C562.194 519.105 579.155 588.637 614.565 650.705C649.976 712.773 702.638 765.277 767.465 803.144C832.291 841.011 907.09 862.961 984.634 866.873C1062.18 870.785 1139.84 856.527 1210.13 825.476C1280.41 794.425 1340.94 747.632 1385.86 689.617C1391.02 682.953 1395.95 676.168 1400.66 669.27C1463.73 576.779 1542.33 481.151 1652.71 462.006Z" fill="url(#paint0_linear_506_8935)"/> <defs> <linearGradient id="paint0_linear_506_8935" x1="957.189" y1="116.257" x2="1063.66" y2="1389.06" gradientUnits="userSpaceOnUse"> <stop stopColor="#803C79"/> <stop offset="1" stopColor="#A54D9D"/> </linearGradient> </defs> </svg>)})`,
    // backgroundPosition: 'center',
    // backgroundSize: 'cover',
    // backgroundRepeat: 'no-repeat'
}

const mobileStyling = {
    ...pcStyling,
    flexDirection: 'column' as 'column',
}

const HowDidYouHear = () => {
    const userId = useSelector(
        (state: RootState) => state.user.auth.status?.uid
    )
    const accountState = useSelector(
        (state: RootState) => state.user.accountState
    )
    const fieldsLoaded = useSelector(
        (state: RootState) => state.userReg.dataLoaded
    )
    const hdyh = useSelector(
        (state: RootState) => state.userReg.fields.howDidYouHear
    )
    const [open, setOpen] = useState(false)

    const openCondition = useMemo(() => {
        // Only open form once when user has submitted their account
        // Wait until all fields have loaded, then check if user has submitted the HDYH form
        if (!fieldsLoaded) {
            return false
        }
        // if(!hdyh) { return true; }
        return (
            !hdyh?.lastRequest && accountState === TeacherAccountState.SUBMITTED
        )
    }, [fieldsLoaded, hdyh, accountState])

    const isMobile = useMediaQuery('(max-width: 600px)')
    const [result, setResult] = useState<HowDidYouHearOptions>()
    const details = useTextInput({
        inputValidator: (input: string) => {
            return getErrorMsgForTextInput(input, 'Details', null, null, true)
        },
    })

    const handleSubmit = async () => {
        if (!userId) {
            return
        }
        const res = await submitHowDidYouHear(userId, {
            lastRequest: new Date().getTime() / 1000,
            details: details.value,
            result: result,
            timeStamp: new Date().getTime() / 1000,
        })
        if (res.code === 200) {
            setOpen(false)
        } else {
            logger.error(`error: ${res.error}`)
        }
    }

    const handleClose = async () => {
        if (!userId) {
            return
        }
        const res = await submitHowDidYouHear(userId, {
            lastRequest: new Date().getTime() / 1000,
            timeStamp: new Date().getTime() / 1000,
        })
        if (res.code === 200) {
            setOpen(false)
        } else {
            logger.error(`error: ${res.error}`)
        }
    }

    return (
        // <Modal open={open || openCondition} onClose={()=>{}}>
        <Modal open={open || openCondition} onClose={() => {}}>
            <Stack sx={isMobile ? mobileStyling : pcStyling}>
                <Stack className={styles.hydhTitleContainer}>
                    <img src={sparkLogo} alt={'Spark Relief Teachers'} />
                    <Stack gap={pxToRem(12)}>
                        <h1>
                            HOW DID YOU HEAR ABOUT <span>SPARK?</span>
                        </h1>
                        <p>
                            We would love to know how you heard about Spark
                            Relief Teachers!
                        </p>
                    </Stack>
                </Stack>
                <Stack className={styles.hdyhForm}>
                    <TeacherRegFormControl variant="standard">
                        <InputLabel>Where did you hear about us?</InputLabel>
                        <Select
                            value={result}
                            onChange={(e) =>
                                setResult(
                                    e.target.value as HowDidYouHearOptions
                                )
                            }
                        >
                            {Object.values(HowDidYouHearOptions).map(
                                (value, i) => {
                                    return (
                                        <MenuItem
                                            key={`howdidyouhearoptions_${i}`}
                                            value={value}
                                        >
                                            {value}
                                        </MenuItem>
                                    )
                                }
                            )}
                        </Select>
                    </TeacherRegFormControl>
                    <UserRegTextField
                        value={details.value}
                        onChange={(e) => details.setValue(e.target.value)}
                        multiline
                        rows={3}
                        placeholder="Tell us a little more if you like..."
                    />
                    <PrimaryButton
                        onClick={handleSubmit}
                        sx={{
                            width: '100%',
                            minHeight: pxToRem(52),
                            height: pxToRem(52),
                            borderRadius: pxToRem(40),
                        }}
                    >
                        SUBMIT
                    </PrimaryButton>
                </Stack>
                <IconButton
                    onClick={handleClose}
                    className={styles.hdyhCloseBtn}
                >
                    <svg
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        {' '}
                        <path
                            d="M24 8L8 24M8 8L24 24"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />{' '}
                    </svg>
                </IconButton>
                <svg
                    className={styles.hydhBackground}
                    viewBox="0 0 1158 669"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    {' '}
                    <path
                        d="M1652.71 462.006C1819.02 433.159 1973.06 573.299 1944.46 739.612C1923.54 861.287 1892.26 978.949 1841.78 1044.14C1739.41 1176.36 1617.38 1223.73 1457.2 1294.49C1297.02 1365.26 1120.02 1397.76 943.293 1388.84C766.57 1379.92 596.102 1329.9 448.361 1243.6C300.62 1157.3 180.6 1037.64 99.8989 896.188C19.1977 754.734 -19.4566 596.267 -12.329 436.098C-5.20142 275.93 47.4671 119.475 140.587 -18.1493C189.297 -90.1378 248.213 -155.701 315.532 -213.26C420.873 -303.328 575.46 -258.26 648.235 -140.066L681.417 -86.1749C744.618 16.4703 699.868 149.824 632.419 249.508C591.559 309.895 568.449 378.546 565.321 448.825C562.194 519.105 579.155 588.637 614.565 650.705C649.976 712.773 702.638 765.277 767.465 803.144C832.291 841.011 907.09 862.961 984.634 866.873C1062.18 870.785 1139.84 856.527 1210.13 825.476C1280.41 794.425 1340.94 747.632 1385.86 689.617C1391.02 682.953 1395.95 676.168 1400.66 669.27C1463.73 576.779 1542.33 481.151 1652.71 462.006Z"
                        fill="url(#paint0_linear_506_8935)"
                    />{' '}
                    <defs>
                        {' '}
                        <linearGradient
                            id="paint0_linear_506_8935"
                            x1="957.189"
                            y1="116.257"
                            x2="1063.66"
                            y2="1389.06"
                            gradientUnits="userSpaceOnUse"
                        >
                            {' '}
                            <stop stopColor="#803C79" />{' '}
                            <stop offset="1" stopColor="#A54D9D" />{' '}
                        </linearGradient>{' '}
                    </defs>{' '}
                </svg>
                {/* <svg width="1158" height="669" viewBox="0 0 1158 669" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M1652.71 462.006C1819.02 433.159 1973.06 573.299 1944.46 739.612C1923.54 861.287 1892.26 978.949 1841.78 1044.14C1739.41 1176.36 1617.38 1223.73 1457.2 1294.49C1297.02 1365.26 1120.02 1397.76 943.293 1388.84C766.57 1379.92 596.102 1329.9 448.361 1243.6C300.62 1157.3 180.6 1037.64 99.8989 896.188C19.1977 754.734 -19.4566 596.267 -12.329 436.098C-5.20142 275.93 47.4671 119.475 140.587 -18.1493C189.297 -90.1378 248.213 -155.701 315.532 -213.26C420.873 -303.328 575.46 -258.26 648.235 -140.066L681.417 -86.1749C744.618 16.4703 699.868 149.824 632.419 249.508C591.559 309.895 568.449 378.546 565.321 448.825C562.194 519.105 579.155 588.637 614.565 650.705C649.976 712.773 702.638 765.277 767.465 803.144C832.291 841.011 907.09 862.961 984.634 866.873C1062.18 870.785 1139.84 856.527 1210.13 825.476C1280.41 794.425 1340.94 747.632 1385.86 689.617C1391.02 682.953 1395.95 676.168 1400.66 669.27C1463.73 576.779 1542.33 481.151 1652.71 462.006Z" fill="url(#paint0_linear_506_8935)"/> <defs> <linearGradient id="paint0_linear_506_8935" x1="957.189" y1="116.257" x2="1063.66" y2="1389.06" gradientUnits="userSpaceOnUse"> <stop stopColor="#803C79"/> <stop offset="1" stopColor="#A54D9D"/> </linearGradient> </defs> </svg> */}
            </Stack>
        </Modal>
    )
}

export default HowDidYouHear
